import { t } from '../utils/i18n'
import styles from './_announcement.module.scss'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import Link from 'next/link'
import Image from 'next/legacy/image'
import {
  CommunityConsolidatedNotification,
  CommunityNotificationTypeEnum
} from '../__generated__/graphql'
import {
  labCommunityCommentPath,
  labCommunityLeaderboardPath,
  labCommunityMemberPath,
  labCommunityPostPath,
  labCoursePath,
  labEventsPath,
  labPath
} from '../utils/routeHelpers'
import LevelUpModal from '../pages/lab/components/LevelUpModal'
import classNames from 'classnames'
import {
  COMMUNITY_CONSOLIDATED_NOTIFICATIONS_DETAILS_QUERY,
  COMMUNITY_PROFILE_CONSOLIDATED_NOTIFICATIONS_QUERY,
  COMMUNITY_PROFILE_QUERY,
  TOGGLE_COMMUNITY_CONSOLIDATED_NOTIFICATION_READ_MUTATION
} from '../gql'
import { getOperationName } from 'apollo-boost'
import { Dispatch, SetStateAction, useRef, useState } from 'react'
import dayjs from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import relativeTime from 'dayjs/plugin/relativeTime'
import useScrollLoading from '../utils/hooks/useScrollLoading'
import LoadingRing from './LoadingRing'

dayjs.extend(relativeTime)
dayjs.extend(updateLocale)

dayjs.updateLocale('en', {
  relativeTime: {
    past: '%s ago',
    s: '%ds',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1m',
    MM: '%dmo',
    y: '1y',
    yy: '%dy'
  }
})

const customFromNow = (date: dayjs.ConfigType) => {
  const now = dayjs()
  const diffInDays = now.diff(date, 'day')

  if (diffInDays >= 7 && diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7)
    return `${weeks}w ago`
  }

  return dayjs(date).fromNow()
}

interface DropdownProps {
  userId: number
  announcements: CommunityConsolidatedNotification[]
  isRemoveReadFilter: boolean
  loadMoreAnnouncements: () => void
  isLoading: boolean
  setLabAnnouncements: Dispatch<
    SetStateAction<CommunityConsolidatedNotification[]>
  >
}

const getAnnouncementLink = (
  announcement: CommunityConsolidatedNotification
) => {
  switch (announcement.actionType) {
    case CommunityNotificationTypeEnum.PostLiked:
    case CommunityNotificationTypeEnum.PostMention:
    case CommunityNotificationTypeEnum.NewPostTopic:
    case CommunityNotificationTypeEnum.FollowedProfileAction:
      return (
        announcement.resource &&
        labCommunityPostPath(announcement.resource.post)
      )
    case CommunityNotificationTypeEnum.NewFollower:
      return (
        announcement.resource &&
        labCommunityMemberPath(announcement.resource.user)
      )
    case CommunityNotificationTypeEnum.CommentReply:
    case CommunityNotificationTypeEnum.PostReply:
    case CommunityNotificationTypeEnum.CommentLiked:
    case CommunityNotificationTypeEnum.CommentMention:
    case CommunityNotificationTypeEnum.FollowedPost:
      return (
        announcement.resource &&
        labCommunityCommentPath(
          announcement.resource.post,
          announcement.resource.comment.id
        )
      )
    case CommunityNotificationTypeEnum.NewLabEvent:
      return labEventsPath()
    case CommunityNotificationTypeEnum.LevelUp:
      return labCommunityLeaderboardPath()
    case CommunityNotificationTypeEnum.CourseShared:
      return (
        announcement.resource?.labCourse &&
        labCoursePath(announcement.resource.labCourse.id)
      )
  }
}

const getNotificationTitle = (
  notification: CommunityConsolidatedNotification,
  memberName?: string
) => {
  let actionPerformer
  if (memberName) {
    actionPerformer = memberName
  } else if (notification.actionPerformers.length === 1) {
    actionPerformer = notification.actionPerformers[0].firstName
  } else {
    actionPerformer = notification.actionPerformersCount + ' ' + t('people')
  }

  const topicTitle = notification.resource?.post?.category.title

  switch (notification.actionType) {
    case CommunityNotificationTypeEnum.PostLiked:
      return actionPerformer + ' ' + t('liked your post')
    case CommunityNotificationTypeEnum.PostReply:
      return actionPerformer + ' ' + t('replied to your post')
    case CommunityNotificationTypeEnum.CommentReply:
      return actionPerformer + ' ' + t('replied to your comment')
    case CommunityNotificationTypeEnum.NewFollower:
      return t('You have a new Follower')
    case CommunityNotificationTypeEnum.CommentMention:
      return t('Someone mentioned you')
    case CommunityNotificationTypeEnum.NewPostTopic:
      return t('New post in ') + topicTitle
    case CommunityNotificationTypeEnum.CommentLiked:
      return actionPerformer + ' ' + t('liked your comment')
    case CommunityNotificationTypeEnum.PostMention:
      return t('Someone mentioned you in post')
    case CommunityNotificationTypeEnum.FollowedPost:
      return t('New comment on post')
    case CommunityNotificationTypeEnum.FollowedProfileAction:
      return actionPerformer + t(' posted in ') + topicTitle
    case CommunityNotificationTypeEnum.LevelUp: {
      const level = notification.resource?.levelInfo?.level
      return t('You reached Level ') + level + '!'
    }
    case CommunityNotificationTypeEnum.NewLabEvent:
      return t('A new LAB+ Event has been added!')
    case CommunityNotificationTypeEnum.CourseShared:
      return actionPerformer + ' ' + t('has shared a course with you')
    default:
      return null
  }
}

const VISIBLE_MEMBERS_NUMBER = 4

const LabNotice: React.FC<{
  announcement: CommunityConsolidatedNotification
  setLabAnnouncements: Dispatch<
    SetStateAction<CommunityConsolidatedNotification[]>
  >
  onOpenLevelUpModal: (level: number) => void
}> = ({ announcement, setLabAnnouncements, onOpenLevelUpModal }) => {
  const isExpandButtonShown =
    announcement.actionPerformersCount > VISIBLE_MEMBERS_NUMBER

  const membersShort = announcement.actionPerformers

  const [members, setMembers] = useState([])
  const [isMembersListExpanded, setIsMembersListExpanded] = useState(false)

  const [queryNotificationDetails] = useLazyQuery(
    COMMUNITY_CONSOLIDATED_NOTIFICATIONS_DETAILS_QUERY,
    {
      variables: { notificationId: announcement.id }
    }
  )

  const [toggleRead] = useMutation(
    TOGGLE_COMMUNITY_CONSOLIDATED_NOTIFICATION_READ_MUTATION,
    {
      variables: { notificationId: announcement.id },
      onCompleted: () => {
        setLabAnnouncements((announcements) =>
          announcements.map((a) =>
            a.id === announcement.id ? { ...a, read: !a.read } : a
          )
        )
      },
      refetchQueries: [
        getOperationName(COMMUNITY_PROFILE_CONSOLIDATED_NOTIFICATIONS_QUERY)
      ]
    }
  )

  const openMembersList = (e) => {
    e.preventDefault()
    queryNotificationDetails({
      onCompleted: (data) => {
        setMembers(
          data.communityConsolidatedNotificationDetails.actionPerformers
        )
        setIsMembersListExpanded((isExpanded) => !isExpanded)
      }
    })
  }

  const markAnnouncementAsRead = () => {
    if (!announcement.read) {
      toggleRead({
        variables: {
          notificationId: announcement.id,
          read: true
        }
      })
    }
  }

  // Temporary fix
  const url = getAnnouncementLink(announcement) || labPath()
  const isLevelUpNotification =
    announcement.actionType === CommunityNotificationTypeEnum.LevelUp

  const handleNoticeClick = (e: React.MouseEvent) => {
    if (!isLevelUpNotification) return

    markAnnouncementAsRead()

    e.preventDefault()

    const level = announcement.resource?.levelInfo?.level
    if (level) {
      onOpenLevelUpModal(level)
    }
  }

  return (
    <>
      <div
        className={classNames(styles.notice, {
          [styles.noticeInactive]: announcement.read
        })}
      >
        <div
          className={classNames(styles.noticeIndicator, {
            [styles.noticeIndicatorInactive]: announcement.read
          })}
          onClick={() => toggleRead()}
        />
        <Link
          href={url}
          className={styles.noticeInfo}
          onClick={handleNoticeClick}
        >
          <div
            className={classNames(styles.noticeText, styles.noticeTextNotify)}
            onClick={isLevelUpNotification ? undefined : markAnnouncementAsRead}
          >
            {membersShort
              .slice(0, VISIBLE_MEMBERS_NUMBER)
              .map((member, index) => {
                return (
                  <div
                    key={member.id}
                    style={{ zIndex: membersShort.length - index }}
                    className={styles.memberPictureContainer}
                  >
                    <Image
                      key={member.id}
                      src={member.picture}
                      width={36}
                      height={36}
                      alt={`${member.firstName} ${member.lastName}`}
                      className={styles.memberPicture}
                    />
                  </div>
                )
              })}
            {isExpandButtonShown && (
              <button className={styles.moreButton} onClick={openMembersList}>
                {isMembersListExpanded ? '-' : '+'}
              </button>
            )}
            <p className={styles.noticeTitle}>
              <span>{getNotificationTitle(announcement)}</span>
            </p>
          </div>

          <p className={classNames(styles.noticeTitle, styles.noticeDate)}>
            {customFromNow(announcement.createdAt)}
          </p>
        </Link>
      </div>

      <div
        className={classNames(styles.membersListWrapper, {
          [styles.expanded]: isMembersListExpanded
        })}
      >
        <div className={styles.membersList}>
          {members.map((member) => (
            <div
              key={member.id}
              className={classNames(styles.notice, {
                [styles.noticeInactive]: announcement.read
              })}
            >
              <Link href={url} onClick={handleNoticeClick}>
                <div
                  className={classNames(
                    styles.noticeText,
                    styles.noticeTextNotify
                  )}
                >
                  <div className={styles.memberPictureContainer}>
                    <Image
                      key={member.id}
                      src={member.picture}
                      width={36}
                      height={36}
                      alt={`${member.firstName} ${member.lastName}`}
                      className={classNames(styles.memberPicture)}
                    />
                  </div>

                  <p className={styles.noticeTitle}>
                    <span>
                      {getNotificationTitle(announcement, member.firstName)}
                    </span>
                  </p>
                  <p
                    className={classNames(
                      styles.noticeTitle,
                      styles.noticeDate,
                      styles.memberNoticeDate
                    )}
                  >
                    {customFromNow(member.notifiedAt)}
                  </p>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

const LabAnnouncementsDropdown: React.FC<DropdownProps> = ({
  userId,
  announcements,
  isRemoveReadFilter,
  loadMoreAnnouncements,
  isLoading,
  setLabAnnouncements
}) => {
  const [levelUpModalLevel, setLevelUpModalLevel] = useState<number | null>(
    null
  )

  const { data: levelUpProfileData } = useQuery(COMMUNITY_PROFILE_QUERY, {
    variables: { userId },
    skip: levelUpModalLevel === null
  })

  const notRead = announcements.filter((notice) => !notice.read)
  const announcementList = isRemoveReadFilter ? notRead : announcements

  const labAnnouncementsRef = useRef(null)

  useScrollLoading(labAnnouncementsRef, loadMoreAnnouncements, [announcements])

  return (
    <>
      <div className={styles.notificationItems} ref={labAnnouncementsRef}>
        {announcementList.map((announcement) => (
          <LabNotice
            key={announcement.id}
            announcement={announcement}
            setLabAnnouncements={setLabAnnouncements}
            onOpenLevelUpModal={setLevelUpModalLevel}
          />
        ))}
        {isLoading && <LoadingRing size={32} />}
      </div>

      {levelUpModalLevel && (
        <LevelUpModal
          userId={userId}
          level={levelUpModalLevel}
          allVotesCount={
            levelUpProfileData?.communityProfile?.communityProfile
              ?.allVotesCount ?? 0
          }
          closeModal={() => setLevelUpModalLevel(null)}
        />
      )}
    </>
  )
}

export default LabAnnouncementsDropdown
